// Habillage de l'app aux couleurs du club (D-024) : couleur, logo, nom.
// Une seule couleur choisie par le club ; ses variantes lisibles (clair /
// sombre, fond / texte) sont calculées par domain/brand.ts et posées en
// variables CSS. Le dernier club est gardé sur l'appareil pour s'afficher dès
// l'ouverture, écran de connexion compris.
import { contrast, isHexColor, monogram, palette } from '../domain/brand';
import type { ClubBrand } from '../lib/api';
import { html, type Html } from './dom';
import { applyAppHead } from './app-icon';

const KEY = 'arceau.club.v1';
const DEFAULT = '#E8590C';   // orange Arceau, quand le club n'a pas encore choisi

export function applyBrand(b: ClubBrand | null, persist = true): void {
  const color = b && isHexColor(b.primary_color) ? b.primary_color : DEFAULT;
  const p = palette(color);
  const s = document.documentElement.style;
  s.setProperty('--club-l-fill', p.light.fill);
  s.setProperty('--club-l-ink', p.light.ink);
  s.setProperty('--club-l-text', p.light.text);
  s.setProperty('--club-d-fill', p.dark.fill);
  s.setProperty('--club-d-ink', p.dark.ink);
  s.setProperty('--club-d-text', p.dark.text);
  // Couleur secondaire (D-081) : sur-titres, chiffres, jauges, onglet actif. Sans elle, rien ne change.
  const sec = b && isHexColor(b.secondary_color) ? b.secondary_color : null;
  document.documentElement.classList.toggle('has-club2', !!sec);
  if (sec) {
    const q = palette(sec);
    s.setProperty('--club2-l-fill', q.light.fill); s.setProperty('--club2-l-text', q.light.text);
    s.setProperty('--club2-d-fill', q.dark.fill); s.setProperty('--club2-d-text', q.dark.text);
    // Sur le bandeau (fond principal) : la secondaire si elle s'y lit, sinon l'encre du bandeau ; en sombre, le bandeau
    // change de teinte : le même choix sur ses couleurs sombres.
    s.setProperty('--club2-on-fill', contrast(q.light.fill, p.light.fill) >= 2.6 ? q.light.fill : p.light.ink);
    s.setProperty('--club2-on-dfill', contrast(q.dark.fill, p.dark.fill) >= 2.6 ? q.dark.fill : p.dark.ink);
  } else for (const k of ['--club2-l-fill', '--club2-l-text', '--club2-d-fill', '--club2-d-text', '--club2-on-fill', '--club2-on-dfill']) s.removeProperty(k);
  document.title = b ? (b.short_name?.trim() || b.name) : 'Arceau';
  applyAppHead(b);   // icône et nom sur l'écran d'accueil (D-102)
  if (!persist) return;
  try { if (b) localStorage.setItem(KEY, JSON.stringify(b)); else localStorage.removeItem(KEY); } catch { /* navigation privée */ }
}

export function storedBrand(): ClubBrand | null {
  try {
    const b = JSON.parse(localStorage.getItem(KEY) ?? 'null') as ClubBrand | null;
    return b && typeof b.id === 'string' && typeof b.name === 'string' ? b : null;
  } catch { return null; }
}

/** Logo du club, sinon son monogramme aux couleurs du club, sinon le ballon Arceau. */
export function clubMark(b: ClubBrand | null, size: 'sm' | 'md' | 'lg' = 'sm'): Html {
  if (!b) return html`<span class="ball ${size === 'sm' ? 'ball-sm' : ''}" aria-hidden="true"></span>`;
  if (b.logo_url) return html`<img class="club-mark club-mark-${size}" src="${b.logo_url}" alt="" aria-hidden="true">`;
  return html`<span class="club-mark club-mark-${size} club-mono" aria-hidden="true">${monogram(b.name, b.short_name)}</span>`;
}

/**
 * Réduit une image choisie par l'admin pour en faire le logo du club :
 * 256 px au plus, WebP (repli PNG), sous la limite acceptée par la base.
 */
export async function logoFromFile(file: File): Promise<string> {
  if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error('format');
  const bitmap = await createImageBitmap(file);
  for (const side of [256, 192, 128]) {
    const k = Math.min(1, side / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * k));
    canvas.height = Math.max(1, Math.round(bitmap.height * k));
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    let url = canvas.toDataURL('image/webp', 0.9);
    if (!url.startsWith('data:image/webp')) url = canvas.toDataURL('image/png');
    if (url.length <= 270000) return url;
  }
  throw new Error('trop lourd');
}

/** Couleurs dominantes d'un logo (data URL), pour proposer principale et secondaire. */
export async function logoColors(src: string | null | undefined, max = 4): Promise<string[]> {
  if (!src) return [];
  const img = new Image();
  img.src = src;
  try { await img.decode(); } catch { return []; }
  const n = 48, cv = document.createElement('canvas');
  cv.width = n; cv.height = n;
  const g = cv.getContext('2d', { willReadFrequently: true });
  if (!g) return [];
  g.drawImage(img, 0, 0, n, n);
  const px = g.getImageData(0, 0, n, n).data;
  const count = new Map<number, { n: number; r: number; g: number; b: number }>();
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3]! < 160) continue;                                    // transparent
    const r = px[i]!, gg = px[i + 1]!, b = px[i + 2]!;
    if (r > 235 && gg > 235 && b > 235) continue;                     // fond blanc
    const k = ((r >> 4) << 8) | ((gg >> 4) << 4) | (b >> 4);          // 4 bits par canal
    const e = count.get(k) ?? { n: 0, r: 0, g: 0, b: 0 };
    e.n++; e.r += r; e.g += gg; e.b += b; count.set(k, e);
  }
  const hex = (v: number) => Math.round(v).toString(16).padStart(2, '0').toUpperCase();
  const out: { c: string; rgb: [number, number, number] }[] = [];
  for (const e of [...count.values()].sort((a, b) => b.n - a.n)) {
    const rgb: [number, number, number] = [e.r / e.n, e.g / e.n, e.b / e.n];
    if (out.some(o => Math.hypot(o.rgb[0] - rgb[0], o.rgb[1] - rgb[1], o.rgb[2] - rgb[2]) < 70)) continue;   // trop proche
    out.push({ c: `#${hex(rgb[0])}${hex(rgb[1])}${hex(rgb[2])}`, rgb });
    if (out.length >= max) break;
  }
  return out.map(o => o.c);
}
